import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import { Modal, Btn, Field, Input, Select } from "@/components/erp/ui";
import { PackageCheck } from "lucide-react";

const ACTIONS = [
  ["RETURN", "Retur & minta supplier kirim yang sesuai", "Sisa dibuat PO baru ke supplier yang sama"],
  ["EMPTY", "Stok supplier kosong", "PO direvisi; kebutuhan dibuka lagi sebagai PR"],
  ["SUBSTITUTE", "Ganti filament lain", "Sisa dibuat PO baru dengan filament pengganti"],
];

// Cek barang datang: terima yang sesuai, sisanya retur / kosong / ganti
export const PoReceiveModal = ({ po, onClose, onDone }) => {
  const [qty, setQty] = useState(0);
  const [action, setAction] = useState("RETURN");
  const [sub, setSub] = useState({ id: "", price: 0 });
  const [note, setNote] = useState("");
  const [stock, setStock] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (po) { setQty(po.qty); setAction("RETURN"); setNote(""); setConfirm(false); setSub({ id: "", price: po.unit_price }); } }, [po]);
  useEffect(() => { if (po && action === "SUBSTITUTE" && !stock.length) { api.get("/stock").then(({ data }) => setStock(data)).catch(() => {}); } }, [po, action, stock.length]);
  if (!po) return null;
  const rem = Math.max(0, +(po.qty - (+qty || 0)).toFixed(4));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/receive`, { qty_ok: +qty || 0, action, substitute_stock_id: sub.id, substitute_price: +sub.price || 0, note });
      toast.success(`Diterima ${data.received_qty} ${po.unit}${data.new_po ? ` · sisa → ${data.new_po.code}` : data.new_pr ? ` · sisa → ${data.new_pr.code}` : ""}`);
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); setConfirm(false); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Terima Barang · ${po.code}`}
      footer={confirm
        ? <><span className="text-sm text-slate-700 mr-auto" data-testid="receive-confirm-text">Apakah betul {+qty || 0} {po.unit} sudah diterima & sesuai?</span><Btn variant="ghost" onClick={() => setConfirm(false)}>Batal</Btn><Btn data-testid="receive-confirm-yes" disabled={busy} onClick={submit}>Ya, sudah diterima</Btn></>
        : <><Btn variant="ghost" onClick={onClose}>Tutup</Btn><Btn data-testid="receive-next" disabled={rem > 0 && action === "SUBSTITUTE" && !sub.id} onClick={() => setConfirm(true)}><PackageCheck className="w-4 h-4" /> Terima</Btn></>}>
      <div className="space-y-3 text-sm">
        <div className="rounded-lg bg-slate-50 p-3 grid grid-cols-2 gap-1">
          <span className="text-slate-500">Item</span><b>{po.stock_name}</b>
          <span className="text-slate-500">Dipesan</span><b>{po.qty} {po.unit}</b>
          <span className="text-slate-500">Supplier</span><span>{po.supplier_name}</span>
          <span className="text-slate-500">Total</span><span>{formatIDR(po.total)}</span>
        </div>
        <Field label={`Jumlah yang diterima & sesuai (${po.unit})`}><Input type="number" data-testid="receive-qty" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
        {rem > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="receive-remainder">
            <div className="font-semibold text-amber-800">{rem} {po.unit} tidak lengkap / tidak sesuai — tindakan:</div>
            {ACTIONS.map(([k, l, d]) => (
              <label key={k} className="flex gap-2 items-start cursor-pointer" data-testid={`receive-action-${k}`}>
                <input type="radio" checked={action === k} onChange={() => setAction(k)} className="mt-1" />
                <span><b>{l}</b><br /><span className="text-xs text-slate-500">{d}</span></span>
              </label>
            ))}
            {action === "SUBSTITUTE" && (
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><Select data-testid="receive-sub-stock" value={sub.id} onChange={(e) => setSub({ ...sub, id: e.target.value })}>
                  <option value="">— filament pengganti —</option>{stock.filter((s) => s.id !== po.stock_id).map((s) => <option key={s.id} value={s.id}>{s.name} {s.variant}</option>)}
                </Select></div>
                <Input type="number" data-testid="receive-sub-price" value={sub.price} onChange={(e) => setSub({ ...sub, price: e.target.value })} placeholder="Harga/unit" />
              </div>
            )}
            <Input data-testid="receive-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (mis. 2 roll warna salah)" />
            <p className="text-[11px] text-slate-500">PO ini akan direvisi (R+1) menjadi {+qty || 0} {po.unit}.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
